<!--
 * @Description: 登录页面
-->

<template>
  <div class="login-container">
    <div class="title-container">
      <h3 class="title">
        {{ t('login.title') }}
      </h3>
      <LangSelect
        v-if="false"
        :isWhite="true"
        class="set-language"
      />
    </div>
    <el-form
      v-if="step === 1"
      class="login-form"
      ref="loginFormRef"
      :model="loginForm"
      :rules="loginRules"
      autocomplete="on"
      label-position="left"
      @submit.prevent="handleLoginVerify"
    >
      <div class="logo-container">
        <el-image
          class="logo"
          :src="Logo"
          fit="contain"
        />
      </div>
      <el-form-item prop="username">
        <el-input
          ref="userNameRef"
          v-model="loginForm.username"
          :placeholder="t('login.username')"
          name="username"
          type="text"
          tabindex="1"
          autocomplete="on"
          prefix-icon="el-icon-user"
        />
      </el-form-item>

      <el-tooltip
        v-model="capsTooltip"
        :content="t('common.onCapsLock')"
        placement="right"
        hide-after="0"
        manual
      >
        <el-form-item prop="password">
          <el-input
            :key="passwordType"
            ref="passwordRef"
            v-model="loginForm.password"
            :type="passwordType"
            :placeholder="t('login.password')"
            name="password"
            tabindex="2"
            autocomplete="on"
            @keyup="checkCapslock"
            @keyup.enter="handleLoginVerify"
            prefix-icon="el-icon-lock"
          />
        </el-form-item>
      </el-tooltip>
      <el-button
        :loading="loading"
        type="primary"
        style="width:100%; margin-bottom:30px;"
        @click="handleLoginVerify"
      >
        {{ t('login.logIn') }}
      </el-button>
    </el-form>
    <el-form
      v-else-if="step === 2"
      class="login-form"
      ref="mfaFormRef"
      :model="mfaForm"
      :rules="mfaFormRules"
      label-position="left"
      @submit.prevent="handleVerifyMFA"
    >
      <div class="logo-container">
        <el-image
          class="logo"
          alt="logo"
          fit="contain"
          :src="Logo"
        />
      </div>
      <el-form-item
        prop="mfaCode"
        :label="t('mfa.mfaChecksum')"
      >
        <el-input
          ref="mfaCodeRef"
          v-model="mfaForm.mfaCode"
          name="mfaCode"
          type="text"
          maxlength="6"
          show-word-limit
          clearable
        />
      </el-form-item>
      <el-row :gutter="30">
        <el-col :span="12">
          <el-button
            @click.prevent="handleBack"
            style="width:100%; margin-bottom:30px;"
          >
            {{ t('common.back') }}
          </el-button>
        </el-col>
        <el-col :span="12">
          <el-button
            :loading="loading"
            type="primary"
            @click="handleVerifyMFA"
            style="width:100%; margin-bottom:30px;"
          >
            {{ t('login.logIn') }}
          </el-button>
        </el-col>
      </el-row>
    </el-form>
    <el-form
      v-else-if="step === 3"
      class="login-form"
      ref="mfaFormRef"
      :model="mfaForm"
      :rules="mfaFormRules"
      label-position="left"
      @submit.prevent="handleBind"
    >
      <div class="logo-container">
        <el-image
          v-if="qrCodeSrc"
          class="logo"
          alt="MFA QR Code"
          fit="contain"
          :src="qrCodeSrc"
        />
      </div>
      <div class="mfa-qrCode-img-tip">
        <div class="mfa-tip">
          {{ t('mfa.mfaBindTip') }}
        </div>
      </div>
      <el-form-item
        prop="mfaCode"
        :label="t('mfa.mfaChecksum')"
      >
        <el-input
          ref="mfaCodeRef"
          v-model="mfaForm.mfaCode"
          name="mfaCode"
          type="text"
          maxlength="6"
          show-word-limit
          clearable
        />
      </el-form-item>
      <el-row :gutter="30">
        <el-col :span="12">
          <el-button
            @click.prevent="handleBind"
            style="width:100%; margin-bottom:30px;"
          >
            {{ t('common.back') }}
          </el-button>
        </el-col>
        <el-col :span="12">
          <el-button
            :loading="loading"
            type="primary"
            style="width:100%; margin-bottom:30px;"
            @click="handleBind"
          >
            {{ t('mfa.bind') }}
          </el-button>
        </el-col>
      </el-row>
    </el-form>
    <div class="footer-links">
      <el-link
        href="https://beian.miit.gov.cn/"
        target="_blank"
      >
        苏ICP备2026026075号
      </el-link>
      <el-link
        href="https://beian.mps.gov.cn/#/query/webSearch"
        target="_blank"
      >
        苏公网安备32041102001822号
      </el-link>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  onMounted,
  reactive,
  watch,
  ref,
  toRefs
} from 'vue'
import LangSelect from '@/components/lang_select/Index.vue'
import { useRoute, LocationQuery, useRouter } from 'vue-router'
import { useStore } from '@/store'
import { UserActionTypes } from '@/store/modules/user/action-types'
import { useI18n } from 'vue-i18n'
import Logo from '@/assets/images/login/carpenter_logo_shape_large.png'
import { generateMfaQrCode, loginVerify } from '@/apis/user'
import { RequestParams } from 'axios-mapper'
import { ElMessage } from 'element-plus'

export default defineComponent({
  components: {
    LangSelect
  },
  setup() {
    const userNameRef = ref(null)
    const passwordRef = ref(null)
    const loginFormRef = ref(null)
    const mfaFormRef = ref(null)
    const mfaCodeRef = ref(null)
    const router = useRouter()
    const route = useRoute()
    const store = useStore()
    const { t } = useI18n()
    const temporaryToken = ref(null as string | null)
    const state = reactive({
      loginForm: {
        username: '',
        password: ''
      },
      loginRules: {
        username: [{ validator: userNameRef, trigger: 'blur' }],
        password: [{ validator: passwordRef, trigger: 'blur' }]
      },
      mfaForm: {
        mfaCode: ''
      },
      mfaFormStruct: [
        { prop: 'comment', label: null, component: 'el-input', type: 'text', model: '' }
      ],
      mfaFormRules: {
        mfaCode: [{ required: true, message: t('mfa.mfaChecksumP'), trigger: 'change' }]
      },
      passwordType: 'password',
      qrCodeSrc: '' as string | null,
      loading: false,
      step: 1,
      capsTooltip: false,
      redirect: '',
      otherQuery: {}
    })

    function tokenExpiredTip() {
      ElMessage({
        message: 'token 过期,请重新登录',
        type: 'error',
        duration: 2000
      })
      state.step = 1
    }

    const methods = reactive({
      checkCapslock: (e: KeyboardEvent) => {
        if (e && typeof e.getModifierState === 'function') {
          state.capsTooltip = e.getModifierState('CapsLock')
        }
      },
      async handleLoginVerify() {
        try {
          await (loginFormRef.value as any).validate()
          state.loading = true
          const params = {
            userAccount: state.loginForm.username,
            userPassword: state.loginForm.password
          }
          const data = await loginVerify(params)
          if (data && data.code === 200 && data.data) {
            temporaryToken.value = data.data.temporaryToken
            if (data.data.isBindMFA) {
              state.qrCodeSrc = null
              state.step = 2
            } else {
              const param: RequestParams = {
                width: 300,
                height: 300
              }
              state.qrCodeSrc = await generateMfaQrCode(data.data.temporaryToken, param)
              state.step = 3
            }
          } else if (data && data.code !== 200) {
            const errorMsg = data.msg ? data.msg : '登录失败'
            ElMessage({
              message: errorMsg,
              type: 'error',
              duration: 2000
            })
          }
          // Just to simulate the time of the request
          setTimeout(() => {
            state.loading = false
          }, 0.5 * 1000)
        } catch (error) {
          console.log(error)
        }
      },
      async handleBind() {
        await (mfaFormRef.value as any).validate()
        if (!temporaryToken.value) {
          tokenExpiredTip()
          return
        }
        const mfaInfo = {
          temporaryToken: temporaryToken.value,
          mfaCode: state.mfaForm.mfaCode
        }
        try {
          await store.dispatch(UserActionTypes.ACTION_BIND_MFA_LOGIN, mfaInfo)
        } catch (err) {
          ElMessage({
            message: err,
            type: 'error',
            duration: 2000
          })
        }
        router.push({
          path: state.redirect || '/',
          query: state.otherQuery
        }).catch(err => {
          console.log(err)
        })
      },
      async handleVerifyMFA() {
        await (mfaFormRef.value as any).validate()
        if (!temporaryToken.value) {
          tokenExpiredTip()
          return
        }
        const mfaInfo = {
          temporaryToken: temporaryToken.value,
          mfaCode: state.mfaForm.mfaCode
        }
        try {
          await store.dispatch(UserActionTypes.ACTION_MFA_LOGIN, mfaInfo)
        } catch (err) {
          ElMessage({
            message: err,
            type: 'error',
            duration: 2000
          })
        }
        router.push({
          path: state.redirect || '/',
          query: state.otherQuery
        }).catch(err => {
          console.log(err)
        })
      },
      async handleBack() {
        if (state.step === 2 || state.step === 3) {
          state.step = 1
        }
      }
    })

    function getOtherQuery(query: LocationQuery) {
      return Object.keys(query).reduce((acc, cur) => {
        if (cur !== 'redirect') {
          acc[cur] = query[cur]
        }
        return acc
      }, {} as LocationQuery)
    }

    watch(() => route.query, query => {
      if (query) {
        state.redirect = query.redirect?.toString() ?? ''
        state.otherQuery = getOtherQuery(query)
      }
    })

    onMounted(() => {
      if (state.loginForm.username === '') {
        (userNameRef.value as any).focus()
      } else if (state.loginForm.password === '') {
        (passwordRef.value as any).focus()
      } else if (state.mfaForm.mfaCode === '') {
        (mfaCodeRef.value as any).focus()
      }
      const query = route.query
      if (query) {
        state.redirect = query.redirect?.toString() ?? ''
        state.otherQuery = getOtherQuery(query)
      }
    })

    return {
      userNameRef,
      passwordRef,
      loginFormRef,
      mfaFormRef,
      mfaCodeRef,
      Logo,
      ...toRefs(state),
      ...toRefs(methods),
      t
    }
  }
})
</script>

<style lang="scss">
// References: https://www.zhangxinxu.com/wordpress/2018/01/css-caret-color-first-line/

//.login-container {
//  .el-input {
//    display: inline-block;
//    height: 47px;
//    width: 85%;
//
//    input {
//      height: 47px;
//      background: transparent;
//      border: 0px;
//      border-radius: 0px;
//      padding: 12px 5px 12px 15px;
//      color: $lightGray;
//      caret-color: $loginCursorColor;
//      -webkit-appearance: none;
//
//      //&:-webkit-autofill {
//      //  box-shadow: 0 0 0px 1000px white inset !important;
//      //}
//    }
//  }
//
//  .el-form-item {
//    border: 1px solid rgba(255, 255, 255, 0.1);
//    background: rgba(0, 0, 0, 0.1);
//    border-radius: 5px;
//    color: #454545;
//  }
//}
</style>

<style
  lang="scss"
  scoped
>
.login-container {
  height: 100%;
  width: 100%;
  overflow: hidden;
  background-color: $loginBg;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 10%;
  position: relative; /* 添加相对定位 */

  video {
    position: absolute;
    /* Vertical and Horizontal center*/
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: -1;
  }

  .login-form {
    position: relative;
    width: 600px;
    max-width: 100%;
    padding: 0 35px 0;
    margin: 0 auto;
    overflow: hidden;
    background-color: #F2F2F2;
    border-radius: 4px;
  }

  .logo-container {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 30px;
  }

  .logo {
    margin-top: 20px;
    width: 200px;
  }

  .tips {
    font-size: 14px;
    color: #fff;
    margin-bottom: 10px;

    span {
      &:first-of-type {
        margin-right: 16px;
      }
    }
  }

  .title-container {
    //margin-top: 140px;
    //position: relative;

    .title {
      font-size: 26px;
      color: $lightGray;
      margin: 0 auto 40px auto;
      text-align: center;
      font-weight: bold;
    }

    .set-language {
      color: #fff;
      position: absolute;
      top: 3px;
      font-size: 18px;
      right: 0;
      cursor: pointer;
    }
  }

  .rebind-mfa-btn {
    background: none;
    border: none;
    color: #0a76a4;
    margin-bottom: 20px;
    padding: 0;
    font-size: 12px;
    width: 100%;
  }

  .footer-links {
    position: absolute;
    bottom: 20px;
    left: 0;
    right: 0;
    text-align: center;
    display: flex;
    justify-content: center;
    gap: 20px;

    .el-link {
      color: #FFFFFF;

      &:hover {
        color: #66b1ff;
      }
    }
  }
}
</style>
